/**
 * Analyses API Schemas
 *
 * Pure Zod schemas for API request/response validation.
 * SINGLE SOURCE OF TRUTH for API contract.
 *
 * NOTE: Schemas match actual backend implementation in edge-api.
 */

import { z } from 'zod/v4';
import { qualityWeightsAtAnalysisSchema, storedQualityWeightsSchema } from './projects';

// ============================================================================
// Enum Schemas
// ============================================================================

/**
 * Analysis Processing Status
 *
 * Tracks the lifecycle of LLM-based analysis operations:
 * - pending: Analysis queued but not yet started
 * - processing: LLM actively analyzing transcript/content
 * - completed: Analysis finished successfully with results
 * - failed: Analysis encountered an error (provider issue, invalid input, etc.)
 *
 * Used in RecordPipelineWorkflow to track analysis progress.
 * Status transitions: pending → processing → (completed | failed)
 */
export const analysisStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
]);

/**
 * Annotation Type Categories
 *
 * Human-added annotations on analysis results for quality assurance:
 * - correction: LLM made an error that needs correction (e.g., wrong sentiment)
 * - highlight: Important moment worth noting (e.g., objection handled well)
 * - note: General observation or context (e.g., customer was upset)
 * - improvement: Suggestion for agent improvement (e.g., missed upsell opportunity)
 * - issue: Compliance or quality concern (e.g., forgot disclaimer)
 *
 * Used in QA workflows where supervisors review and annotate analyses.
 * Annotations can have position markers (startPosition/endPosition) to reference
 * specific parts of the transcript.
 */
export const annotationTypeSchema = z.enum([
  'objection_handling',
  'discovery_gap',
  'tone_issue',
  'correction',
  'highlight',
  'note',
  'improvement',
  'issue',
  'other',
]);

/**
 * Annotation Severity Levels
 *
 * Impact classification for annotations (especially issues and improvements):
 * - low: Minor observation, nice-to-have improvement
 * - medium: Moderate issue, should be addressed (default)
 * - high: Significant problem, needs attention
 * - critical: Severe compliance violation or quality failure
 *
 * Severity determines urgency in QA workflows and coaching prioritization.
 * Critical annotations may trigger immediate alerts or incident reports.
 */
export const annotationSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);

/**
 * Review status enum - matches actual backend implementation
 */
export const reviewStatusSchema = z.enum(['pending', 'reviewed', 'flagged']);

/** Promote any existing immutable version; promoting an older row is rollback. */
export const promoteAnalysisVersionRequestSchema = z
  .object({
    expectedCurrentAnalysisId: z.string().uuid().nullable(),
  })
  .strict();

// ============================================================================
// Param Schemas
// ============================================================================

/**
 * Path parameter schema for analysis ID
 */
export const analysisIdParamSchema = z.object({
  id: z.string().min(1),
});

/**
 * Path parameter schema for annotation ID (includes analysis id)
 */
export const annotationIdParamSchema = z.object({
  id: z.string().min(1),
  annotationId: z.string().min(1),
});

/**
 * Path parameter schema for review ID
 */
export const reviewIdParamSchema = z.object({
  reviewId: z.string(),
});

// ============================================================================
// Query Schemas
// ============================================================================

/**
 * Query schema for listing annotations
 */
export const annotationsQuerySchema = z.object({
  analysisId: z.string().optional(),
  type: annotationTypeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

/**
 * Query schema for listing reviews
 */
export const reviewsQuerySchema = z.object({
  analysisId: z.string().optional(),
  status: reviewStatusSchema.optional(),
  reviewerId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});

// ============================================================================
// Request Schemas - Annotations
// ============================================================================

/**
 * Create Annotation Request
 *
 * Add human annotation to an analysis result.
 * Used by supervisors during QA review to mark issues, highlights, or improvements.
 *
 * Position markers (startPosition/endPosition) reference character offsets
 * in the transcript text, enabling precise highlighting of specific moments.
 * If omitted, annotation applies to the entire analysis.
 *
 * Example use cases:
 * - Mark compliance violation at specific timestamp
 * - Highlight excellent objection handling
 * - Note incorrect sentiment score from LLM
 * - Suggest coaching improvement for missed opportunity
 */
export const createAnnotationRequestSchema = z
  .object({
    type: annotationTypeSchema,
    severity: annotationSeveritySchema.default('medium'),
    note: z.string().min(1).max(5000),
    startPosition: z.number().int().min(0).optional(),
    endPosition: z.number().int().min(0).optional(),
  })
  .strict();

/**
 * Update annotation request
 */
export const updateAnnotationRequestSchema = z
  .object({
    type: annotationTypeSchema.optional(),
    severity: annotationSeveritySchema.optional(),
    note: z.string().min(1).max(5000).optional(),
    startPosition: z.number().int().min(0).optional(),
    endPosition: z.number().int().min(0).optional(),
  })
  .strict();

// ============================================================================
// Request Schemas - Reviews
// ============================================================================

/**
 * Create Review Request
 *
 * Supervisor review of an analysis result.
 * Reviews track the QA workflow: pending → reviewed → flagged.
 *
 * Fields:
 * - status: Review outcome (reviewed = approved, flagged = needs attention)
 * - markers: Array of predefined tags (e.g., ["excellent_communication", "missed_upsell"])
 * - overallFeedback: Free-text summary from supervisor (max 2000 chars)
 *
 * Reviews differ from annotations:
 * - Reviews are high-level approval/rejection of entire analysis
 * - Annotations are specific comments on particular moments or issues
 * - One review per analysis, multiple annotations allowed
 */
export const createReviewRequestSchema = z
  .object({
    status: reviewStatusSchema.default('reviewed').optional(),
    markers: z.array(z.string()).default([]).optional(),
    overallFeedback: z.string().max(2000).optional(),
  })
  .strict();

/**
 * Update review request
 */
export const updateReviewRequestSchema = z
  .object({
    status: reviewStatusSchema.optional(),
    markers: z.array(z.string()).optional(),
    overallFeedback: z.string().max(2000).optional(),
  })
  .strict();

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Diarization segment schema - speaker turn in transcript
 */
export const diarizationSegmentSchema = z.object({
  speaker: z.string().optional(),
  // Mirrors ChatRoleSchema in packages/core/src/schemas/chat.ts. Duplicated
  // (not imported) because @aica/api-types does not depend on @aica/core.
  // Drift from ChatRoleSchema fails packages/core/src/schemas/chat.test.ts.
  role: z.enum(['agent', 'customer', 'system', 'unknown']).optional(),
  start: z.number(),
  end: z.number(),
  text: z.string().optional(),
  confidence: z.number().optional(),
});

/**
 * Transcript schema - nested in analysis detail response
 */
export const transcriptSchema = z.object({
  id: z.string(),
  text: z.string(),
  language: z.string().nullable(),
  diarization: z.array(diarizationSegmentSchema).nullable(),
  confidence: z.union([z.string(), z.number()]).nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  createdAt: z.string().nullable(),
});

/**
 * Analysis Detail Response
 *
 * Complete analysis result from GET /api/v1/analyses/:id.
 * Contains full LLM output including:
 * - metrics: Structured scores (quality, sentiment, compliance, etc.) as key-value pairs
 * - summary: Human-readable summary of the interaction
 * - fullResult: Complete LLM response (may include reasoning, raw scores, etc.)
 * - insights: Additional insights extracted from analysis (suggestions, patterns, etc.)
 * - Cost tracking: tokensIn/tokensOut — the platform owner only, absent for
 *   everyone else (#1341); costCents — a 0 constant for everyone but the owner
 *
 * Transcript is optionally loaded separately via GET /records/:id/transcript
 * to reduce payload size when not needed.
 */
export const analysisResponseSchema = z.object({
  id: z.string(),
  recordId: z.string(),
  createdAt: z.string(),
  metrics: z.record(z.string(), z.number()).nullable(),
  summary: z.string().nullable(),
  fullResult: z.unknown().nullable(),
  insights: z.unknown().nullable(),
  provider: z.string(),
  model: z.string(),
  /**
   * Prompt profile that produced this analysis. Null for analyses run
   * before prompt profiles existed or with the built-in default profile.
   */
  promptProfileId: z.string().nullable(),
  tokensIn: z.number().int().optional(),
  tokensOut: z.number().int().optional(),
  costCents: z.number(),
  // Note: transcript is loaded separately via GET /records/:id/transcript
  transcript: transcriptSchema.nullable().optional(),
  /** True if this analysis belongs to a demo record. Defaults to false. */
  isDemo: z.boolean().default(false),
  /**
   * The record's project's CURRENT quality weights (#874), canonicalized:
   * the object, or `null` — equal weights. The card scores its breakdown with
   * them, so its number is the one the list, the dashboards and the public API
   * show. Absent from an older server — read as equal weights. Read with
   * `storedQualityWeightsSchema`: an odd value becomes `null`, never a failure
   * of the whole detail view.
   */
  qualityWeights: storedQualityWeightsSchema.optional(),
  /**
   * The weights the analysis was finalized with (from its metadata), for the
   * line "scored with the weights of the time of analysis" on the card:
   * an object, `null` (equal weights, recorded as such) or absent (not
   * recorded — an analysis from before weights were stored, or a value that
   * cannot be used). Test absence with `=== undefined`: a spoilt value leaves
   * the key present holding `undefined` (`qualityWeightsAtAnalysisSchema`).
   * Compare with `qualityWeights` only after `canonicalizeQualityWeights` on
   * both — `null`, `3,3,3,3,3,3` and a stored `2,2,2,2,2,2` all mean "equal".
   */
  qualityWeightsAtAnalysis: qualityWeightsAtAnalysisSchema,
});

/**
 * Inferred type for the detailed analysis response. Consumers should
 * prefer this over hand-rolled interfaces (SSOT rule).
 */
export type AnalysisResponse = z.infer<typeof analysisResponseSchema>;

/**
 * Voice metrics (WS7-T10) — the interpreted acoustic levels the acoustic
 * service produces for each call, persisted at `fullResult.voiceMetrics`
 * (camelCase). These are aggregate signal characteristics (speaking rate,
 * pauses, pitch variation, voice stability) — NOT biometric identification and
 * NOT emotion inference (the service's deprecated `emotion` field is never
 * persisted; EU AI Act Art. 5(1)(f)).
 *
 * The categorical fields are `z.string()` rather than strict enums on purpose:
 * this is a display-only block parsed in isolation, so an unexpected level from
 * the service should degrade to showing that label, not fail the parse and hide
 * real data.
 */
export const voiceMetricsSchema = z.object({
  loudness: z.object({
    level: z.string(),
    variability: z.string(),
    description: z.string(),
  }),
  // Null when the acoustic service's server-side plausibility gate (H-1)
  // suppressed an implausible WPM rather than surface a garbage verdict.
  speechRate: z
    .object({
      wpm: z.number(),
      level: z.string(),
      description: z.string(),
    })
    .nullable(),
  pauses: z.object({
    count: z.number(),
    // Mean length of one unvoiced segment. Optional because analyses written
    // before the acoustic-service pause fix carry `longPauses` instead — a
    // required field here would fail Zod on every historical record and blank
    // the whole analysis panel.
    avgPauseMs: z.number().optional(),
    // 'unknown' = degraded VAD read (every voicing feature at the extractor's 0
    // default) — the service could not read a rhythm. NOT 'none': that is a
    // healthy read with no pauses, and the two must not share a caption.
    rhythm: z
      .enum(['unknown', 'none', 'smooth', 'normal', 'choppy', 'hesitation'])
      .optional(),
    // Superseded by avgPauseMs: eGeMAPS functionals cannot count long pauses,
    // so this was a fixed 20%-of-count estimate that resolved to 0 every time.
    // Kept optional so historical records still validate; never written now.
    longPauses: z.number().optional(),
    speakingRatio: z.number(),
    description: z.string(),
  }),
  pitch: z.object({
    variability: z.string(),
    rangeHz: z.number(),
    description: z.string(),
  }),
  voiceQuality: z.object({
    stability: z.string(),
    breathiness: z.string(),
    description: z.string(),
  }),
  acousticSummary: z.string(),
});

export type VoiceMetrics = z.infer<typeof voiceMetricsSchema>;

export const getAnalysisDetailResponseSchema = z.object({
  success: z.boolean().optional(),
  analysis: analysisResponseSchema,
});

// ============================================================================
// Annotation Response Schemas
// ============================================================================

export const annotationResponseSchema = z.object({
  id: z.string(),
  analysisId: z.string(),
  recordId: z.string(),
  createdByUserId: z.string(),
  type: annotationTypeSchema,
  severity: annotationSeveritySchema,
  note: z.string(),
  startPosition: z.number().int().nullable(),
  endPosition: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * The list read also says what the reader may do (#2915): `canAnnotate` — the
 * write gate admits them on this analysis; a row's `canEdit` — they may edit
 * and delete it (the author, while `canAnnotate` holds).
 */
export const listAnnotationsResponseSchema = z.object({
  annotations: z.array(annotationResponseSchema.extend({ canEdit: z.boolean() })),
  canAnnotate: z.boolean(),
});

export const createAnnotationResponseSchema = z.object({
  annotation: annotationResponseSchema,
});

// ============================================================================
// Review Response Schemas
// ============================================================================

export const reviewResponseSchema = z.object({
  id: z.string(),
  analysisId: z.string(),
  recordId: z.string(),
  reviewedByUserId: z.string(),
  status: reviewStatusSchema,
  markersJson: z.string(),
  markers: z.array(z.string()).optional(),
  overallFeedback: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const listReviewsResponseSchema = z.object({
  reviews: z.array(reviewResponseSchema),
});

export const createReviewResponseSchema = z.object({
  review: reviewResponseSchema,
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type AnalysisStatus = z.infer<typeof analysisStatusSchema>;
export type AnnotationType = z.infer<typeof annotationTypeSchema>;
export type AnnotationSeverity = z.infer<typeof annotationSeveritySchema>;
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;
export type PromoteAnalysisVersionRequest = z.infer<
  typeof promoteAnalysisVersionRequestSchema
>;
export type AnnotationsQuery = z.infer<typeof annotationsQuerySchema>;
export type ReviewsQuery = z.infer<typeof reviewsQuerySchema>;
export type DiarizationSegment = z.infer<typeof diarizationSegmentSchema>;
export type Transcript = z.infer<typeof transcriptSchema>;
// AnalysisResponse already exported above (next to the schema definition).
export type AnnotationResponse = z.infer<typeof annotationResponseSchema>;
export type ListAnnotationsResponse = z.infer<typeof listAnnotationsResponseSchema>;
export type CreateAnnotationRequest = z.infer<typeof createAnnotationRequestSchema>;
export type UpdateAnnotationRequest = z.infer<typeof updateAnnotationRequestSchema>;
export type ReviewResponse = z.infer<typeof reviewResponseSchema>;
export type CreateReviewRequest = z.infer<typeof createReviewRequestSchema>;
export type UpdateReviewRequest = z.infer<typeof updateReviewRequestSchema>;
